import React, {useState, useEffect, useRef} from 'react';
import { useTranslation } from 'react-i18next';


const Sidebar = ({
  modules = [],
  selectedModuleId,
  onModuleSelect,
  course,
  completedModules = [],
  openIds,
  toggleOpen,
  courseName = ""
}) => {
  const [isSidebarVisible, setSidebarVisible] = useState(window.innerWidth >= 991);
  const [isSidebarBtnActive, setSidebarBtnActive] = useState(window.innerWidth < 991);
  const [openModules, setOpenModules] = useState(modules.length > 0 ? [modules[0].module_id] : []);
  const sidebarRef = useRef(null);
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const currentLang = course?.lang;

  const getLangCode = (lang) => {
    switch (lang) {
      case 'English':
        return 'en'; 
      case 'Hindi':
        return 'hi';
      case 'Marathi':
        return 'mar';
      default:
        return 'en';
    }
  };

  const langId = getLangCode(currentLang);
  const tForLang = i18n.getFixedT(langId);

  console.log('Sidebar course status:', course?.course_status);

   useEffect(() => {
    if (modules && modules.length > 0) {
      // First check if any module has is_started flag
      const startedModule = modules.find(element => element.is_started);
      
      if (startedModule) {
        // If a module is marked as started, open that one
        setOpenModules([startedModule._id]);
      } else {
        // Find the first module that has incomplete sub-modules
        const moduleWithIncompleteSubModules = modules.find(module => {
          if (!module.sub_modules || module.sub_modules.length === 0) return false;
          return !areAllSubModulesCompleted(module);
        });
        
        if (moduleWithIncompleteSubModules) {
          setOpenModules([moduleWithIncompleteSubModules.module_id]);
        } else {
          // If all modules are completed, open the first one
          setOpenModules([modules[0].module_id]);
        }
      }
    } 
  }, [modules, completedModules]);

  // useEffect(() => {
  //   const started = modules.find(m => m.is_started);
  //   if (started) setOpenModules([started._id]);
  // }, [modules]);

  const toggleModule = (moduleId) => {
    setOpenModules((prev) =>
      prev.includes(moduleId) ? prev.filter(id => id !== moduleId) : [...prev, moduleId]
    );
  };

  const handleToggleSidebar = () => {
    setSidebarVisible(!isSidebarVisible);
    setSidebarBtnActive(!isSidebarBtnActive);
  };

 const handleModuleClick = (module) => {
    console.log('Sidebar module clicked:', module);
    
    // Check if user can access this module
    const canAccessModule = canUserAccessModule(module);
    
    if (!canAccessModule) {
      console.log('Access denied to module:', module.module_id);
      return; // Prevent navigation to inaccessible modules
    }
    
    // Allow navigation to accessible modules
    onModuleSelect(module);
    
    // Only toggle the parent module if it has sub_modules
    if (module.sub_modules && module.sub_modules.length > 0) {
      toggleModule(module.module_id);
    }
    
    // Hide sidebar on mobile after selection
    if (window.innerWidth < 991) {
      setSidebarVisible(false);
    }
  };

  // Function to check if user can access a module
  const canUserAccessModule = (module) => {
    // If module is completed, user can access it
    if (module.is_completed || completedModules.includes(module.module_id)) {
      return true;
    }
    
    // Get all modules in sequential order
    const flatModules = flattenModules(modules);
    const moduleIndex = flatModules.findIndex(m => m.module_id === module.module_id);
    
    if (moduleIndex === -1) return false;
    
    // User can access the first module
    if (moduleIndex === 0) return true;
    
    // User can access a module if all previous modules are completed
    for (let i = 0; i < moduleIndex; i++) {
      const prevModule = flatModules[i];
      if (!prevModule.is_completed && !completedModules.includes(prevModule.module_id)) {
        return false;
      }
    }
    
    return true;
  };

  // Helper function to flatten nested modules (same as in dashboard - sub-modules only)
  const flattenModules = (modules) => {
    let result = [];
    
    if (!modules || !Array.isArray(modules)) return result;
    
    for (const module of modules) {
      // Only include sub_modules, not the main module
      if (module.sub_modules && Array.isArray(module.sub_modules)) {
        result = result.concat(module.sub_modules);
        
        // Also check for sub_sub_modules
        module.sub_modules.forEach(subModule => {
          if (subModule.sub_sub_modules && Array.isArray(subModule.sub_sub_modules)) {
            result = result.concat(subModule.sub_sub_modules);
          }
        });
      }
    }
    
    return result;
  };

  // Helper function to check if all sub-modules of a main module are completed
  const areAllSubModulesCompleted = (module) => {
    if (!module.sub_modules || module.sub_modules.length === 0) {
      return false;
    }
    
    for (const subModule of module.sub_modules) {
      const isSubModuleCompleted = subModule.is_completed || completedModules.includes(subModule.module_id);
      if (!isSubModuleCompleted) {
        return false;
      }
      
      // Check sub_sub_modules if they exist
      if (subModule.sub_sub_modules && subModule.sub_sub_modules.length > 0) {
        for (const subSubModule of subModule.sub_sub_modules) {
          const isSubSubModuleCompleted = subSubModule.is_completed || completedModules.includes(subSubModule.module_id);
          if (!isSubSubModuleCompleted) {
            return false;
          }
        }
      }
    }
    
    return true;
  };

  const getModuleIcon = (mod) => {
    // For sub-modules and sub_sub_modules, show completed icon if they are completed
    if (mod.is_completed || course?.course_status === 'completed' || completedModules.includes(mod.module_id)) {
      return <img src="/images/completed.svg" alt="completed" className="w-10 me-2 completed" />;
    }

    // Check for content_type or module_type
    const moduleType = mod.content_type || mod.module_type;
    
    switch (moduleType) {
      case 'video':
        return <img src="/images/video.svg" alt="video" className="me-2 w-10" />;
      case 'test':
        return <img src="/images/quiz.svg" alt="quiz" className="me-2 w-10" />;
      case 'document':
        return <img src="/images/notes.svg" alt="document" className="me-2 w-10" />;
      case 'carousal':
        return <img src="/images/notes.svg" alt="carousel" className="me-2 w-10" />;
      case 'content':
      default:
        return <img src="/images/notes.svg" alt="notes" className="me-2 w-10" />;
    }
  };

  return (
    <div>
       <button
        className={`sidebar-btn font-18 fw-bold primary_color text-decoration-none pt-4 mb-4 ${isSidebarBtnActive ? 'active' : ''
          }`}
        onClick={handleToggleSidebar}
      >
        <span className="me-2"> </span>
        {isSidebarVisible ? (
          <>
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 33 33" fill="none">
              <path
                d="M6.99958 17.6668L12.3329 23.0001L10.4663 24.8934L1.90625 16.3334L10.4663 7.77344L12.3329 9.66677L6.99958 15.0001H28.3329V17.6668H6.99958ZM28.3329 8.33344V11.0001H14.9996V8.33344H28.3329ZM28.3329 21.6668V24.3334H14.9996V21.6668H28.3329Z"
                fill="#fff"
              /></svg>{' '} {tForLang('course_modules_hide_menu')}
          </>
        ) : (
          <>
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 33 32" fill="none">
              <path d="M25.7778 17.3328L20.4444 22.6661L22.3111 24.5595L30.8711 15.9995L22.3111 7.43945L20.4444 9.33279L25.7778 14.6661H4.44443V17.3328H25.7778ZM4.44443 7.99945V10.6661H17.7778V7.99945H4.44443ZM4.44443 21.3328V23.9995H17.7778V21.3328H4.44443Z" fill="#146B90" />
            </svg>{' '}
            
          </>
        )}
        
      </button>

      <div
        className="moduleSidebar"
        style={{
          width: isSidebarVisible ? '270px' : '0',
          overflowX: 'hidden',
          padding: isSidebarVisible ? '20px' : '0',
          transition: 'width 0.3s',
        }}
      >
        <div ref={sidebarRef} className="module-list">
          <h1 className="font-18 fw-bold mt-5 ms-4 pt-5 pb-4 text-white course-title">
            <span style={{ 
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              width: '242px',
              minHeight: '40px',
              overflow:'hidden',
              display: 'flex',
              alignItems: 'center',
              opacity: '1',
              transition: 'background-color 0.3s, opacity 0.3s' 
            }}> <img
            src='/images/module_icon.png'
            alt="icon"
            style={{ height: '40px', width: '40px' }}
          />
              {courseName}
            </span>
          </h1>

          <div className="accordion px-2">
            {modules?.map((module) => (
              <div key={module.module_id} className={`accordion-item ${openModules.includes(module.module_id) ? 'open' : ''}`}>
                <div className="accordion-header py-4 ps-4 d-flex justify-content-between align-items-center" onClick={() => toggleModule(module.module_id)}>
                  <div className="d-flex align-items-center">
                    {/* <span className="me-2">
                      {areAllSubModulesCompleted(module) ? (
                        <img src="/images/completed.svg" alt="completed" className="w-10 completed" />
                      ) : (
                        <img src="/images/stack.svg" alt="stack" className="w-10" />
                      )}
                    </span> */}
                    <span className={`accordion-title text-white fw-bold font-16 ${selectedModuleId === module.module_id ? 'selected' : ''}`}>
                      {module.name}
                    </span>
                  </div>
                  <div>
                    {openModules.includes(module.module_id) ? (
                        <svg xmlns="http://www.w3.org/2000/svg" width="33" height="32" viewBox="0 0 33 32" fill="none">
                        <path d="M8.33301 20.002L16.333 12.002L24.333 20.002" stroke="#fff" strokeWidth="2.4" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                     ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="19" height="12" viewBox="0 0 19 12" fill="none" className='ms-2'>
                      <path d="M1.66626 2.00195L9.66626 10.002L17.6663 2.00195" stroke="white" strokeWidth="2.4" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                     )}
                  </div>
                </div>

                <div className={`accordion-content px-4 ${openModules.includes(module.module_id) ? 'open' : ''}`}>
                  {module.sub_modules?.map(sub => {
                    const canAccess = canUserAccessModule(sub);
                    const isCompleted = sub.is_completed || completedModules.includes(sub.module_id);
                    
                    return (
                      <div key={sub.module_id} className="my-lg-4 my-3">
                        <div
                          className={`submodule ${selectedModuleId === sub.module_id ? 'active' : ''}`}
                          style={selectedModuleId === sub.module_id ? {
                              backgroundColor: 'rgba(255, 255, 255, 0.2)',
                              width: '230px',
                              minHeight: '40px',
                              marginBottom: '2.5rem',
                              overflow:'hidden',
                              display: 'flex',
                              alignItems: 'center',
                              opacity: '1',
                              transition: 'background-color 0.3s, opacity 0.3s',
                              cursor: 'pointer'
                            } : {
                              backgroundColor: 'transparent',
                              width: '230px',
                              minHeight: '40px',
                              overflow:'hidden',
                              opacity: canAccess ? '0.8' : '0.4',
                              cursor: canAccess ? 'pointer' : 'not-allowed'
                            }}                      
                          onClick={() => canAccess && handleModuleClick(sub)}
                        >
                          <div className="submodule-title text-white font-14 d-flex align-items-center">
                            <span className="sidetab-img">{getModuleIcon(sub)}</span>
                            <span style={{ opacity: canAccess ? '1' : '0.6' }}>
                              {sub.name}
                              {/* {!canAccess && !isCompleted && (
                                <span className="ms-2" title="Complete previous modules to unlock">
                                  🔒
                                </span>
                              )} */}
                            </span>
                          </div>
                        </div>

                        {sub.sub_sub_modules && (
                          <div className="nested-submodule ps-5">
                            {sub.sub_sub_modules.map(nested => {
                              const canAccessNested = canUserAccessModule(nested);
                              const isNestedCompleted = nested.is_completed || completedModules.includes(nested.module_id);
                              
                              return (
                                <div
                                  key={nested.module_id}
                                  className={`nestedsubmodule my-lg-4 pt-1 ps-3 ${selectedModuleId === nested.module_id ? 'active' : ''}`}
                                  style={{
                                    opacity: canAccessNested ? '0.8' : '0.4',
                                    cursor: canAccessNested ? 'pointer' : 'not-allowed'
                                  }}
                                  onClick={() => canAccessNested && handleModuleClick(nested)}
                                >
                                  <div className="nestedsubmodule-title text-white font-14 d-flex align-items-start">
                                    <span className="sidetab-img">{getModuleIcon(nested)}</span>
                                    <span style={{ opacity: canAccessNested ? '1' : '0.6' }}>
                                      {nested.name}
                                      {/* {!canAccessNested && !isNestedCompleted && (
                                        <span className="ms-2" title="Complete previous modules to unlock">
                                          🔒
                                        </span>
                                      )} */}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;




